// List rows shared by Today, Tasks, Call reviews and the lead hub. Same
// markup as taskRow / stepRow / callRow in the prototype.

import { useSetTaskDone } from '../lib/data';
import { fDT, fTime } from '../lib/format';
import type { BoardLead, Call, Task } from '../lib/types';
import { Icon } from '../ui/icons';
import { Avatar, DueText, useDrawer, useToast } from '../ui/kit';

export function TaskRow({
  task,
  lead,
  showLead = true,
}: {
  task: Task;
  lead?: BoardLead | undefined;
  showLead?: boolean;
}) {
  const done = Boolean(task.done_at);
  const setDone = useSetTaskDone();
  const toast = useToast();
  const { open } = useDrawer();

  function toggle() {
    setDone.mutate(
      { task: task.id, done: !done },
      {
        onSuccess: () => {
          if (!done) toast('Task done', () => setDone.mutate({ task: task.id, done: false }));
        },
        onError: (e) => toast(e.message),
      },
    );
  }

  return (
    <div className={`trow ${done ? 'done' : ''}`}>
      <button
        className={`check ${done ? 'on' : ''}`}
        aria-label={done ? 'Mark as not done' : 'Mark as done'}
        onClick={toggle}
        disabled={setDone.isPending}
      />
      <div>
        <button className="t tlink" onClick={() => open({ type: 'task', id: task.id })}>
          {task.text}
        </button>
        <div className="m">
          {task.origin.startsWith('Internal')
            ? 'Internal'
            : task.origin === 'Automation'
              ? 'Automation'
              : 'Promise'}
          {showLead && lead && (
            <>
              {' · '}
              <button onClick={() => open({ type: 'lead', id: lead.id })}>{lead.clinic}</button>
            </>
          )}
        </div>
      </div>
      <Avatar id={task.owner_id} />
      <DueText due={task.due_at} done={done} />
    </div>
  );
}

export function StepRow({ lead }: { lead: BoardLead }) {
  const { open } = useDrawer();
  const late = new Date(lead.step_due!) < new Date();
  const kind = /call/i.test(lead.step_text ?? '') ? 'Call' : 'Follow-up';
  return (
    <div className="trow">
      <button
        className="check"
        aria-label="Mark step done"
        onClick={() => open({ type: 'lead', id: lead.id, edit: 'complete' })}
      />
      <div>
        <div className="t">{lead.step_text}</div>
        <div className="m">
          {kind} ·{' '}
          <button onClick={() => open({ type: 'lead', id: lead.id })}>{lead.clinic}</button>
        </div>
      </div>
      <Avatar id={lead.step_owner} />
      {late ? (
        <span className="tm late">Overdue</span>
      ) : (
        <span className="tm">{fTime(lead.step_due!)}</span>
      )}
    </div>
  );
}

export function CallRow({ call, linked }: { call: Call; linked: boolean }) {
  const { open } = useDrawer();
  const promises = (call.promises ?? []).filter((p) => p.on).length;
  const pill =
    call.status === 'done' ? (
      <span className="pill te">Confirmed</span>
    ) : !linked ? (
      <span className="pill red">Not linked</span>
    ) : (
      <span className="pill">{promises} promises found</span>
    );
  return (
    <button className="callrow" onClick={() => open({ type: 'call', id: call.id })}>
      <span className="vid">
        <Icon name="video" />
      </span>
      <span>
        <span className="t" style={{ display: 'block' }}>
          {call.title}
        </span>
        <span className="m">
          <Icon name="clock" />
          {fDT(call.started_at)}
          {call.duration_min ? `, ${call.duration_min} min` : ''}
        </span>
      </span>
      {pill}
      <Icon name="chev" />
    </button>
  );
}
